import { useEffect, useState } from 'react';

import { Modal } from '../ui';
import { api } from '../../lib/api';
import type { Preview } from './types';
import { errorText } from './types';

/**
 * Shows exactly what will change on the host before anything is sent. The apply
 * itself needs the typed phrase, and the host rolls back unless someone confirms.
 */
export function ApplyDialog({
  open,
  onClose,
  hostname,
  onApplied,
}: {
  open: boolean;
  onClose: () => void;
  hostname: string | null;
  onApplied: () => void;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [typed, setTyped] = useState('');
  const [timeoutSec, setTimeoutSec] = useState(120);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expected = `APPLY FIREWALL ${hostname ?? ''}`;

  useEffect(() => {
    if (!open) return;
    setTyped('');
    setError(null);
    setPreview(null);
    setLoading(true);
    api<Preview>('/connectiq/firewall/preview')
      .then(setPreview)
      .catch((err) => setError(errorText(err)))
      .finally(() => setLoading(false));
  }, [open]);

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('/connectiq/firewall/apply', { method: 'POST', body: JSON.stringify({ confirm: typed, timeoutSec }) });
      onApplied();
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const canApply = !!preview?.applyable && typed === expected && !busy;

  return (
    <Modal open={open} onClose={onClose} title="Apply rules to the host" wide>
      {loading && <p className="ciq-muted">Reading the rules…</p>}
      {preview && (
        <>
          <p>
            This changes the managed network only. The host keeps the previous rules unless you confirm within the window, and restores them
            automatically if you do not. Administration stays reachable through the guard rules.
          </p>
          {preview.warnings.length > 0 && (
            <ul className="ciq-warnings">
              {preview.warnings.map((w) => (
                <li key={w}>▲ {w}</li>
              ))}
            </ul>
          )}
          <div className="ciq-diff">
            <div>
              <div className="ciq-eyebrow">Adds ({preview.added.length})</div>
              <pre className="mono">{preview.added.length ? preview.added.join('\n') : 'Nothing new.'}</pre>
            </div>
            <div>
              <div className="ciq-eyebrow">Removes ({preview.removed.length})</div>
              <pre className="mono">{preview.removed.length ? preview.removed.join('\n') : 'Nothing removed.'}</pre>
            </div>
          </div>
          <details>
            <summary>Full ruleset ({preview.lines.length} lines)</summary>
            <pre className="mono">{preview.lines.join('\n')}</pre>
          </details>
          <div className="ciq-row">
            <label className="ciq-field">
              <span>Keep the change for</span>
              <select value={timeoutSec} onChange={(e) => setTimeoutSec(Number(e.target.value))}>
                <option value={60}>60 seconds</option>
                <option value={120}>2 minutes</option>
                <option value={300}>5 minutes</option>
              </select>
            </label>
            <label className="ciq-field">
              <span>Type {expected} to confirm</span>
              <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
            </label>
          </div>
          {!preview.applyable && <p className="ciq-warn">This ruleset cannot be applied yet. Fix the warnings above first.</p>}
        </>
      )}
      <div className="ciq-actions">
        <button type="button" className="ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="button" onClick={apply} disabled={!canApply}>
          {busy ? 'Applying…' : 'Apply with rollback'}
        </button>
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
    </Modal>
  );
}
